import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Divider,
} from "@mui/material";
import { useAuth } from "../../authContext/authContext";
import { changePassword } from "../../Services/authService";
import { fetchMyProfile, updateMyProfile } from "../../Services/apiService";
import {
  fetchTherapistApplicationStatus,
  uploadTherapistApplyDocument,
  submitTherapistApply,
} from "../../Services/therapistApplyService";
import ConsentCheckboxes from "../ConsentCheckboxes";
import TwoFactorSettings from "./TwoFactorSettings";
import { useUnsavedChanges } from "./unsavedChanges";
import PasswordStrengthChecklist from "../PasswordStrengthChecklist";
import TherapistApplyFields from "../therapistApply/TherapistApplyFields";
import { isPasswordValid } from "../../utils/passwordRules";
import { AUTH_ERROR } from "../../theme/appTheme";
import { useTranslation } from "../../i18n/LanguageContext";

const fieldSx = {
  width: { xs: "100%", sm: 280 },
  "& .MuiOutlinedInput-root": {
    borderRadius: 1.5,
    fontSize: "0.8125rem",
    bgcolor: "#EBEBEB",
    "& fieldset": { borderColor: "#EBEBEB" },
    "&:hover fieldset": { borderColor: "#d8d8d8" },
    "&.Mui-focused fieldset": { borderColor: "#cfcfcf" },
  },
};

const actionButtonSx = {
  textTransform: "none",
  borderRadius: 1.5,
  px: 2.5,
  py: 0.5,
  minHeight: 0,
  fontSize: "0.8125rem",
  color: "#1a1a1a",
  bgcolor: "#EBEBEB",
  boxShadow: "none",
  "&:hover": { bgcolor: "#E0E0E0", boxShadow: "none" },
};

const secondaryButtonSx = {
  textTransform: "none",
  borderRadius: 1.5,
  px: 2.5,
  py: 0.5,
  minHeight: 0,
  fontSize: "0.8125rem",
  color: "#4b5563",
  borderColor: "#d0d5dd",
  bgcolor: "#EBEBEB",
  boxShadow: "none",
  "&:hover": { bgcolor: "#E0E0E0", borderColor: "#c5c5c5", boxShadow: "none" },
};

function Row({ label, children, alignTop }) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: alignTop ? { xs: "column", sm: "row" } : "row",
        alignItems: alignTop ? { xs: "stretch", sm: "flex-start" } : "center",
        justifyContent: "space-between",
        gap: alignTop ? { xs: 1.5, sm: 3 } : 3,
        py: 2,
        borderBottom: "1px solid #e5e7eb",
        "&:last-child": { borderBottom: "none" },
      }}
    >
      <Typography
        sx={{ fontSize: "0.8125rem", color: "#111827", pt: alignTop ? { xs: 0, sm: 0.75 } : 0 }}
      >
        {label}
      </Typography>
      <Box
        sx={{
          flexShrink: 0,
          display: "flex",
          justifyContent: alignTop ? { xs: "flex-start", sm: "flex-end" } : "flex-end",
          width: alignTop ? { xs: "100%", sm: "auto" } : "auto",
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

const emptyTherapistFields = (email) => ({
  practiceName: "",
  practiceCity: "",
  practiceEmail: email || "",
  files: [],
});

export default function AccountSettings({ onClose }) {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [changingPassword, setChangingPassword] = useState(false);
  const [previousPassword, setPreviousPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState([]);
  const [success, setSuccess] = useState("");

  // --- Communication preferences (GET/PUT /profile/me) ---
  const [consentLoading, setConsentLoading] = useState(true);
  const [consent, setConsent] = useState({ consentPortalUpdates: false, consentMarketing: false });
  const [savedConsent, setSavedConsent] = useState({ consentPortalUpdates: false, consentMarketing: false });
  const [consentSaving, setConsentSaving] = useState(false);
  const [consentError, setConsentError] = useState("");
  const [consentSuccess, setConsentSuccess] = useState("");
  // Login-code preference, from the same GET /profile/me — true / false /
  // null (not chosen yet). See TwoFactorSettings.
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(null);
  const [profileLoadFailed, setProfileLoadFailed] = useState(false);

  useEffect(() => {
    let isMounted = true;
    fetchMyProfile()
      .then((res) => {
        if (!isMounted) return;
        const next = {
          consentPortalUpdates: Boolean(res?.data?.consent_portal_updates),
          consentMarketing: Boolean(res?.data?.consent_marketing),
        };
        setConsent(next);
        setSavedConsent(next);
        setTwoFactorEnabled(res?.data?.two_factor_enabled ?? null);
      })
      .catch(() => {
        if (!isMounted) return;
        setConsentError(t("settings.account.communicationPreferences.loadError"));
        setProfileLoadFailed(true);
      })
      .finally(() => {
        if (isMounted) setConsentLoading(false);
      });
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const consentDirty =
    consent.consentPortalUpdates !== savedConsent.consentPortalUpdates ||
    consent.consentMarketing !== savedConsent.consentMarketing;

  const handleSaveConsent = async () => {
    setConsentSaving(true);
    setConsentError("");
    setConsentSuccess("");
    try {
      await updateMyProfile(consent);
      setSavedConsent(consent);
      setConsentSuccess(t("settings.account.communicationPreferences.saved"));
      return true;
    } catch (err) {
      setConsentError(err.message || t("settings.account.communicationPreferences.saveError"));
      return false;
    } finally {
      setConsentSaving(false);
    }
  };

  useUnsavedChanges("communicationPreferences", consentDirty, handleSaveConsent);

  // --- Therapist application (GET /account/therapist-application, apply/re-apply) ---
  const [appStatus, setAppStatus] = useState(null); // { status } | null while loading — status: none | pending | approved | rejected | revoked
  const [appLoading, setAppLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [therapistFields, setTherapistFields] = useState(() => emptyTherapistFields(user?.email));
  const [applySubmitting, setApplySubmitting] = useState(false);
  const [applyError, setApplyError] = useState("");

  useEffect(() => {
    let isMounted = true;
    fetchTherapistApplicationStatus()
      .then((data) => {
        if (isMounted) setAppStatus(data);
      })
      .catch(() => {
        if (isMounted) setAppStatus({ status: "none" });
      })
      .finally(() => {
        if (isMounted) setAppLoading(false);
      });
    return () => {
      isMounted = false;
    };
  }, []);

  const handleStartApply = () => {
    setApplyError("");
    setTherapistFields(emptyTherapistFields(user?.email));
    setApplying(true);
  };

  const handleSubmitApply = async () => {
    if (!therapistFields.practiceName.trim() || !therapistFields.practiceCity.trim() || !therapistFields.practiceEmail.trim()) {
      setApplyError(t("settings.account.therapistApplication.errors.fieldsRequired"));
      return;
    }
    if (therapistFields.files.length === 0) {
      setApplyError(t("settings.account.therapistApplication.errors.docsRequired"));
      return;
    }
    setApplySubmitting(true);
    setApplyError("");
    try {
      const uploaded = [];
      for (const file of therapistFields.files) {
        uploaded.push(await uploadTherapistApplyDocument(file));
      }
      await submitTherapistApply({
        practiceName: therapistFields.practiceName.trim(),
        practiceCity: therapistFields.practiceCity.trim(),
        practiceEmail: therapistFields.practiceEmail.trim(),
        documents: uploaded,
      });
      setAppStatus({ status: "pending" });
      setApplying(false);
    } catch (err) {
      setApplyError(err.message || t("settings.account.therapistApplication.errors.generic"));
    } finally {
      setApplySubmitting(false);
    }
  };

  const handleLogout = () => {
    logout();
    if (onClose) onClose();
    navigate("/login", { replace: true });
  };

  const resetPasswordForm = () => {
    setChangingPassword(false);
    setPreviousPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setError("");
    setFieldErrors([]);
  };

  const handleChangePassword = async () => {
    setError("");
    setFieldErrors([]);
    setSuccess("");

    if (!previousPassword || !newPassword || !confirmPassword) {
      setError(t("settings.account.errors.fillAllFields"));
      return;
    }
    if (newPassword !== confirmPassword) {
      setError(t("settings.account.errors.mismatch"));
      return;
    }
    if (!isPasswordValid(newPassword)) {
      setError(t("settings.account.errors.requirements"));
      return;
    }

    setSaving(true);
    try {
      const res = await changePassword(previousPassword, newPassword);
      setSuccess(res?.message || t("settings.account.passwordUpdated"));
      setPreviousPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setChangingPassword(false);
    } catch (err) {
      if (Array.isArray(err.errors) && err.errors.length) {
        setFieldErrors(err.errors);
      } else {
        setError(err.message || t("settings.account.errors.generic"));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ px: 1, pt: 0.5 }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5, mb: 1.5 }}>
        <Typography sx={{ fontSize: "0.9375rem", fontWeight: 600, color: "#111827" }}>
          {t("settings.account.title")}
        </Typography>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          {error && (
            <Alert severity="error" sx={{ fontSize: "0.8125rem", py: 0 }}>
              {error}
            </Alert>
          )}
          {success && !error && (
            <Alert severity="success" sx={{ fontSize: "0.8125rem", py: 0 }}>
              {success}
            </Alert>
          )}
        </Box>
      </Box>

      <Box>
        <Row label={t("sidebar.account.logout")}>
          <Button variant="contained" onClick={handleLogout} sx={actionButtonSx}>
            {t("sidebar.account.logout")}
          </Button>
        </Row>

        <Row label={t("settings.account.changePassword")} alignTop={changingPassword}>
          {!changingPassword ? (
            <Button
              variant="contained"
              onClick={() => {
                setSuccess("");
                setError("");
                setFieldErrors([]);
                setChangingPassword(true);
              }}
              sx={actionButtonSx}
            >
              {t("settings.account.changePassword")}
            </Button>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25, alignItems: { xs: "stretch", sm: "flex-end" }, width: "100%" }}>
              <TextField
                type="password"
                size="small"
                placeholder={t("settings.account.previousPasswordPlaceholder")}
                value={previousPassword}
                onChange={(e) => setPreviousPassword(e.target.value)}
                autoComplete="current-password"
                sx={fieldSx}
              />
              <TextField
                type="password"
                size="small"
                placeholder={t("settings.account.newPasswordPlaceholder")}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                autoComplete="new-password"
                sx={fieldSx}
              />
              <Box sx={{ width: { xs: "100%", sm: 280 } }}>
                <PasswordStrengthChecklist password={newPassword} sx={{ mt: 0, alignItems: "flex-start" }} />
              </Box>
              {fieldErrors.length > 0 && (
                <Box component="ul" sx={{ width: { xs: "100%", sm: 280 }, m: 0, pl: 2.25 }}>
                  {fieldErrors.map((msg, i) => (
                    <Typography key={i} component="li" variant="caption" sx={{ color: AUTH_ERROR }}>
                      {msg}
                    </Typography>
                  ))}
                </Box>
              )}
              <TextField
                type="password"
                size="small"
                placeholder={t("settings.account.confirmNewPasswordPlaceholder")}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                autoComplete="new-password"
                sx={fieldSx}
              />
              <Box sx={{ display: "flex", gap: 1, mt: 0.5 }}>
                <Button
                  variant="outlined"
                  onClick={resetPasswordForm}
                  disabled={saving}
                  sx={secondaryButtonSx}
                >
                  {t("settings.account.cancel")}
                </Button>
                <Button
                  variant="contained"
                  onClick={handleChangePassword}
                  disabled={saving || !isPasswordValid(newPassword)}
                  sx={actionButtonSx}
                >
                  {saving ? <CircularProgress size={16} sx={{ color: "#1a1a1a" }} /> : t("settings.account.updatePassword")}
                </Button>
              </Box>
            </Box>
          )}
        </Row>
      </Box>

      <Divider sx={{ my: 2.5 }} />

      <TwoFactorSettings
        loading={consentLoading}
        initialValue={twoFactorEnabled}
        loadFailed={profileLoadFailed}
        actionButtonSx={actionButtonSx}
        secondaryButtonSx={secondaryButtonSx}
      />

      <Divider sx={{ my: 2.5 }} />

      <Box sx={{ mb: 1.5 }}>
        <Typography sx={{ fontSize: "0.9375rem", fontWeight: 600, color: "#111827", mb: 2.5 }}>
          {t("settings.account.communicationPreferences.title")}
        </Typography>

        {consentLoading ? (
          <CircularProgress size={18} sx={{ color: "#9ca3af" }} />
        ) : (
          <>
            <ConsentCheckboxes
              consentPortalUpdates={consent.consentPortalUpdates}
              consentMarketing={consent.consentMarketing}
              onChange={setConsent}
            />
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: 2.5, "&:empty": { display: "none" } }}>
              {/* Only offered once something actually changed. */}
              {consentDirty && (
                <Button
                  variant="contained"
                  onClick={handleSaveConsent}
                  disabled={consentSaving}
                  sx={actionButtonSx}
                >
                  {consentSaving ? (
                    <CircularProgress size={16} sx={{ color: "#1a1a1a" }} />
                  ) : (
                    t("settings.account.communicationPreferences.savePreferences")
                  )}
                </Button>
              )}
              {consentError && (
                <Alert severity="error" sx={{ fontSize: "0.8125rem", py: 0 }}>
                  {consentError}
                </Alert>
              )}
              {consentSuccess && !consentError && !consentDirty && (
                <Alert severity="success" sx={{ fontSize: "0.8125rem", py: 0 }}>
                  {consentSuccess}
                </Alert>
              )}
            </Box>
          </>
        )}
      </Box>

      {!appLoading && appStatus?.status !== "approved" && (
        <>
          <Divider sx={{ my: 2.5 }} />

          <Box>
            <Typography sx={{ fontSize: "0.9375rem", fontWeight: 600, color: "#111827", mb: 2.5 }}>
              {t("settings.account.therapistApplication.title")}
            </Typography>

            {appStatus?.status === "pending" && !applying && (
              <Alert severity="info" sx={{ fontSize: "0.8125rem" }}>
                {t("settings.account.therapistApplication.pending")}
              </Alert>
            )}

            {(appStatus?.status === "rejected" || appStatus?.status === "revoked") && !applying && (
              <>
                <Alert severity="error" sx={{ fontSize: "0.8125rem", mb: 1.5 }}>
                  {appStatus.status === "revoked"
                    ? t("settings.account.therapistApplication.revoked")
                    : t("settings.account.therapistApplication.rejected")}
                </Alert>
                <Button variant="contained" onClick={handleStartApply} sx={actionButtonSx}>
                  {t("settings.account.therapistApplication.reapply")}
                </Button>
              </>
            )}

            {appStatus?.status === "none" && !applying && (
              <>
                <Typography sx={{ fontSize: "0.8125rem", color: "#6b7280", mb: 1.5 }}>
                  {t("settings.account.therapistApplication.interested")}
                </Typography>
                <Button variant="contained" onClick={handleStartApply} sx={actionButtonSx}>
                  {t("settings.account.therapistApplication.apply")}
                </Button>
              </>
            )}

            {applying && (
              <Box
                sx={{
                  width: "100%",
                  p: 3,
                  backgroundColor: "#FFFFFF",
                  border: "1px solid #e5e7eb",
                  borderRadius: 2.5,
                  boxShadow: "0 1px 3px rgba(16,24,40,0.06)",
                }}
              >
                <TherapistApplyFields
                  value={therapistFields}
                  onChange={setTherapistFields}
                  disabled={applySubmitting}
                  variant="portal"
                />

                {applyError && (
                  <Alert severity="error" sx={{ fontSize: "0.8125rem", mt: 1.5 }}>
                    {applyError}
                  </Alert>
                )}

                <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={() => setApplying(false)}
                    disabled={applySubmitting}
                    sx={secondaryButtonSx}
                  >
                    {t("settings.account.therapistApplication.cancel")}
                  </Button>
                  <Button variant="contained" onClick={handleSubmitApply} disabled={applySubmitting} sx={actionButtonSx}>
                    {applySubmitting ? (
                      <CircularProgress size={16} sx={{ color: "#1a1a1a" }} />
                    ) : (
                      t("settings.account.therapistApplication.submit")
                    )}
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        </>
      )}
    </Box>
  );
}
